import {
  all,
  call,
  put,
  race,
  select,
  take,
  takeLatest,
  takeEvery,
} from 'redux-saga/effects';

import { DETAILS_SETTINGS_MODAL_TYPES } from 'constants/all';

import { detailsSettingsModalFlow } from 'modules/common/modals/detailsSettingsModal/data/sagas';
import {
  mergeModal,
  types as mergeModalTypes,
} from 'modules/common/modals/mergeModal/data/actions';
import { areQuoteItemsLocked } from 'modules/quotes/collaborators/data/sagas';
import { broadcastQuoteItemWorkflowChange } from 'modules/quotes/quoteDetailsPage/hooks/useSyncQpvAndBaqTabs';
import { componentInterrogation } from 'modules/quotes/quoteItemComponents/data/actions';
import {
  refreshQuoteItem,
  collapseQuoteItem,
  setQuoteItemsUpdating,
  receiveImportFromErpTemporaryQuoteItems,
  receiveQuoteItems,
  removeQuoteItems,
  removeConvertQuoteFileToQuoteItemTasks,
} from 'modules/quotes/quoteItems/data/actions';
import {
  getFilenameByQuoteItemId,
  getPartNumberByQuoteItemId,
  getRevisionByQuoteItemId,
  getRootComponentIdByQuoteItemId,
} from 'modules/quotes/quoteItems/data/attributeSelectors/quoteItems';
import { refreshQuoteItemSaga } from 'modules/quotes/quoteItems/data/sagas';
import { refreshComponent } from 'modules/quotes/quoteItems/data/sagas';
import {
  getQuoteItemsByIdsDenormalized,
  getSortedQuoteItemIds,
  getExpandedQuoteItemId,
  getQuoteItemByIdDenormalized,
  getTemporaryErpQuoteItemIds,
  getConvertQuoteFileToQuoteItemTasks,
} from 'modules/quotes/quoteItems/data/selectors';
import quoteItemService from 'modules/quotes/quoteItems/data/service';
import { makeAssemblyModalFlow } from 'modules/quotes/quoteItems/modals/makeAssemblyModal/data/sagas';

import quoteService from 'services/quoteService';

import { receiveSupplierMaterialFromGetOrCreate } from 'store/materials/actions';
import {
  refreshQuote,
  convertQuoteFilesToLineItems,
} from 'store/quotes/actions';
import {
  getQuoteItems,
  getQuoteNumberWithRevision,
} from 'store/quotes/selectors';
import { getQuoteId } from 'store/quotes/selectors';
import { QUOTE_REFRESH_ERROR, QUOTE_REFRESH_SUCCESS } from 'store/quotes/types';
import { invalidateQuoteHeader } from 'store/quotes/utils';

import { notifyError, notifySuccess, notifyWarning } from 'utils/notifyUtils';

import {
  bulkUpdateRootComponents,
  copyQuoteItemsInQuote,
  deleteQuoteItems,
  makeQuoteItemsAssemblies,
  quoteItemSelect,
  types,
  refreshQuoteItems,
  quoteItemFilter,
} from './actions';
import { getIsFiltered } from './reducer';

export default function* root() {
  yield takeLatest(
    types.COPY_QUOTE_ITEMS_IN_QUOTE_REQUEST,
    watchCopyQuoteItemsInQuote,
  );
  yield takeLatest(types.DELETE_QUOTE_ITEMS_REQUEST, watchDeleteQuoteItems);
  yield takeLatest(
    types.MAKE_QUOTE_ITEMS_ASSEMBLIES_REQUEST,
    watchMakeQuoteItemsAssemblies,
  );
  yield takeLatest(
    types.BULK_UPDATE_COMPONENTS_REQUEST,
    watchBulkUpdateRootComponents,
  );
  yield takeLatest(types.MERGE_QUOTE_ITEMS_REQUEST, watchMergeQuoteItems);
  yield takeLatest(
    types.REFRESH_QUOTE_ITEMS_REQUEST,
    watchRefreshQuoteItemsRequest,
  );
  yield takeLatest(types.QUOTE_ITEM_FILTER_SEARCH, watchQuoteItemFilterSearch);
  yield takeLatest(
    types.REPLACE_TEMP_ERP_QUOTE_ITEM_REQUEST,
    watchReplaceTempErpQuoteItems,
  );
  yield takeEvery(
    types.REPLACE_TEMP_CONVERTED_FROM_QUOTE_FILE_QUOTE_ITEM_REQUEST,
    watchReplaceTempConvertedFromQuoteFileQuoteItems,
  );
}

export function* refreshExpandedQuoteItemSaga() {
  const expandedQuoteItemId = yield select(getExpandedQuoteItemId);

  if (expandedQuoteItemId) {
    yield call(refreshQuoteItemSaga, expandedQuoteItemId);
  }
}

function* watchQuoteItemFilterSearch(action) {
  const { value: search } = action.payload;
  const sortedQuoteItemIds = yield select(getSortedQuoteItemIds);
  const isFiltered = yield select(getIsFiltered);

  if (!isFiltered) {
    yield put(quoteItemFilter.clear());
    return;
  }

  const filteredQuoteItemIds = [];

  for (const quoteItemId of sortedQuoteItemIds) {
    const [revision, filename, partNumber] = yield all([
      select(getRevisionByQuoteItemId, quoteItemId),
      select(getFilenameByQuoteItemId, quoteItemId),
      select(getPartNumberByQuoteItemId, quoteItemId),
    ]);

    if (
      partNumber?.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
      revision?.toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
      filename?.toLocaleLowerCase().includes(search.toLocaleLowerCase())
    ) {
      filteredQuoteItemIds.push(quoteItemId);
    }
  }

  yield put(quoteItemFilter.setFilteredIds(filteredQuoteItemIds));
}

function* watchCopyQuoteItemsInQuote(action) {
  try {
    const quoteId = yield select(getQuoteId);
    const { quoteItemIds } = action.payload;

    // copy quote items
    yield call(quoteService.copyQuoteItemsInQuote, quoteId, { quoteItemIds });

    // refresh the quote
    yield put(refreshQuote.request());
    yield take([QUOTE_REFRESH_SUCCESS, QUOTE_REFRESH_ERROR]);

    yield put(copyQuoteItemsInQuote.success());
  } catch (error) {
    yield put(copyQuoteItemsInQuote.error(error));
  }
}

function* watchDeleteQuoteItems(action) {
  const { quoteItemIds, onSettledCallback } = action.payload;
  try {
    const expandedQuoteItemId = yield select(getExpandedQuoteItemId);
    if (expandedQuoteItemId && quoteItemIds.includes(expandedQuoteItemId)) {
      yield put(collapseQuoteItem.request());
    }

    // make sure we are not deleting a quote item being used by another team member
    if (
      yield call(areQuoteItemsLocked, {
        quoteItemIds,
      })
    ) {
      return;
    }

    // Check if expandedQuoteItem needs to be refreshed after quote is updated
    // This is needed when a deleted quote item contained the same root part as the expanded quote item (and they are not the same quote item)
    // We store values of root component UUIDs flagged for deletion and compare them to expanded quote item root component UUID
    const quoteItemsForDeletion = yield select(
      getQuoteItemsByIdsDenormalized,
      quoteItemIds,
    );
    const quoteItemsForDeletionRootComponentUUIDS = quoteItemsForDeletion.map(
      (quoteItem) => quoteItem?.rootComponent?.part?.uuid,
    );
    let expandedQuoteItemRootComponentUUID;

    // attempt to delete all quote items
    const quoteId = yield select(getQuoteId);
    yield call(quoteService.quoteItems.bulkDelete, quoteId, quoteItemIds);

    // delete went successfully, refetch the quote because positions
    // may have changed
    let quoteItems = yield call(quoteService.getQuoteItemHeadersById, quoteId);
    // If a quote item is expanded and it is not being deleted
    if (expandedQuoteItemId && !quoteItemIds.includes(expandedQuoteItemId)) {
      const existingQuoteItem = yield select(
        getQuoteItemByIdDenormalized,
        expandedQuoteItemId,
      );

      // store expanded quote item root component UUID
      expandedQuoteItemRootComponentUUID =
        existingQuoteItem?.rootComponent?.part?.uuid; // string || undefined

      quoteItems = quoteItems.map((item) =>
        item.id !== expandedQuoteItemId
          ? item
          : { ...existingQuoteItem, position: item.position },
      );
    }

    yield put(receiveQuoteItems(quoteItems));
    yield put(removeQuoteItems(quoteItemsForDeletion));

    // Called AFTER updating the quote
    // If expandedQuoteItem is same part UUID as a deletedQuoteItem part UUID, refetch expandedQuoteItem
    if (
      expandedQuoteItemRootComponentUUID &&
      quoteItemsForDeletionRootComponentUUIDS.includes(
        expandedQuoteItemRootComponentUUID,
      )
    ) {
      yield call(refreshExpandedQuoteItemSaga);
    }

    yield put(deleteQuoteItems.success());
  } catch (error) {
    yield put(deleteQuoteItems.error(error));
  } finally {
    if (onSettledCallback) {
      yield call(onSettledCallback);
    }
  }
}

function* watchMakeQuoteItemsAssemblies(action) {
  try {
    const { quoteItemIds } = action.payload;
    const selectedQuoteItems = yield select(
      getQuoteItemsByIdsDenormalized,
      quoteItemIds,
    );

    let shouldAskAboutOps = false;
    let shouldAskAboutMakeSupportingFile = false;
    for (const quoteItem of selectedQuoteItems) {
      const { rootComponent } = quoteItem;
      if (rootComponent.process) {
        shouldAskAboutOps = true;
      }
      if (!rootComponent.part.isManual && rootComponent.part.hasGeometry) {
        shouldAskAboutMakeSupportingFile = true;
      }
    }

    let shouldRegenerateOps = true;
    let shouldMakeSupportingFileIfHasGeometry = false;
    if (shouldAskAboutOps || shouldAskAboutMakeSupportingFile) {
      const confirmPayload = yield call(makeAssemblyModalFlow, {
        shouldAskAboutOps,
        shouldAskAboutMakeSupportingFile,
      });
      if (confirmPayload === null) {
        return;
      } else {
        shouldRegenerateOps = confirmPayload.shouldRegenerateOps;
        shouldMakeSupportingFileIfHasGeometry =
          confirmPayload.shouldMakeSupportingFileIfHasGeometry;
      }
    }

    // attempt to make assemblies for all quote items
    try {
      for (const quoteItem of selectedQuoteItems) {
        const quoteItemId = quoteItem.id;
        yield call(quoteService.quoteItems.makeAssembly, quoteItemId, {
          shouldRegenerateOps,
          shouldMakeSupportingFileIfHasGeometry,
        });
        yield put(refreshQuoteItem({ quoteItemId, shouldRecalculate: false }));
      }
    } catch (error) {
      /*
      if there is an error we need to attempt to refetch the
      quote because some quote items may have been changed
      */
      const quoteNumberWithRevision = yield select(getQuoteNumberWithRevision);
      invalidateQuoteHeader(quoteNumberWithRevision);
      throw error;
    }

    yield put(makeQuoteItemsAssemblies.success());
  } catch (error) {
    console.error(error);
    yield put(makeQuoteItemsAssemblies.error({ error }));
  }
}

function* watchBulkUpdateRootComponents(action) {
  const { quoteItemIds } = action.payload;

  const updateSettings = yield call(detailsSettingsModalFlow, {
    isMultipleItems: quoteItemIds.length > 1,
    mode: DETAILS_SETTINGS_MODAL_TYPES.BULK_COMPONENT_UPDATE,
  });
  if (!updateSettings) return;

  const { process, material, supplierMaterial, finishOpDefIds } =
    updateSettings;

  yield put(setQuoteItemsUpdating.request({ quoteItemIds }));

  const rootComponentIds = [];
  const broadcastUpdateContexts = {};

  try {
    for (const quoteItemId of quoteItemIds) {
      const rootComponentId = yield select(
        getRootComponentIdByQuoteItemId,
        quoteItemId,
      );
      rootComponentIds.push(rootComponentId);
      broadcastUpdateContexts[rootComponentId] = {
        quoteItemId,
      };
    }
    const quoteId = yield select(getQuoteId);
    const response = yield call(
      quoteItemService.bulkUpdateComponents,
      quoteId,
      {
        componentIds: rootComponentIds,
        processId: process,
        materialId: material,
        supplierMaterialId: supplierMaterial,
        finishOpDefIds,
      },
    );

    const comp = response[0];
    if (comp.material) {
      const newSupplierMaterial = {
        ...comp.material,
        materialId: comp.material.material.id,
      };
      put(
        receiveSupplierMaterialFromGetOrCreate({
          supplierMaterial: newSupplierMaterial,
        }),
      );
    }

    for (const [rootComponentId, broadcastUpdateContext] of Object.entries(
      broadcastUpdateContexts,
    )) {
      yield put(
        componentInterrogation.request({ componentId: rootComponentId }),
      );
      yield call(refreshComponent, rootComponentId);
      broadcastQuoteItemWorkflowChange({
        context: {
          quoteItemId: broadcastUpdateContext.quoteItemId,
          isRootComponent: true,
        },
      });
    }
    yield put(setQuoteItemsUpdating.success({ quoteItemIds }));
    yield put(bulkUpdateRootComponents.success());
  } catch (e) {
    yield put(bulkUpdateRootComponents.error(e));
    yield put(setQuoteItemsUpdating.error({ quoteItemIds }));
  }
}

function* watchMergeQuoteItems(action) {
  const { mergeType, selectedQuoteItems } = action.payload;
  try {
    yield put(mergeModal.open({ mergeType, selectedQuoteItems }));
    const winner = yield race({
      mergeSuccess: take(mergeModalTypes.MERGE_QUOTE_ITEMS_SUCCESS),
      mergeCancel: take(mergeModalTypes.CLOSE_MERGE_MODAL),
    });

    if (winner.mergeSuccess) {
      yield put(quoteItemSelect.deselectAll());
    }
  } catch (error) {
    console.error('error watching merge quote items');
  }
}

function* watchRefreshQuoteItemsRequest(action) {
  const { quoteItemIds } = action.payload;

  yield put(setQuoteItemsUpdating.request({ quoteItemIds }));

  try {
    const quoteItems = yield call(
      quoteItemService.refreshQuoteItems,
      quoteItemIds,
    );

    yield put(quoteItemSelect.deselectAll());
    yield put(setQuoteItemsUpdating.success({ quoteItemIds }));
    yield put(refreshQuoteItems.success({ quoteItems }));
  } catch (error) {
    yield put(setQuoteItemsUpdating.error({ quoteItemIds }));
    yield put(refreshQuoteItems.error(error));
  }
}

function* watchReplaceTempErpQuoteItems(action) {
  const { successes, failures } = action.payload;
  const successTempQuoteItemIds = successes.map(
    (result) => result.tempQuoteItemId,
  );
  const failureTempQuoteItemIds = failures.map(
    (result) => result.tempQuoteItemId,
  );
  const tempErpQuoteItemIdsToRemove = new Set([
    ...successTempQuoteItemIds,
    ...failureTempQuoteItemIds,
  ]);
  const tempErpQuoteItemIds = yield select(getTemporaryErpQuoteItemIds);
  const successTemporaryErpQuoteItems = yield select(
    getQuoteItemsByIdsDenormalized,
    successTempQuoteItemIds,
  );
  const failureTemporaryErpQuoteItems = yield select(
    getQuoteItemsByIdsDenormalized,
    failureTempQuoteItemIds,
  );
  const newTempErpQuoteItemIds = tempErpQuoteItemIds.filter(
    (x) => !tempErpQuoteItemIdsToRemove.has(x),
  );
  yield put(refreshQuote.request());
  yield put(
    receiveImportFromErpTemporaryQuoteItems({
      temporaryErpPartQuoteItemIds: newTempErpQuoteItemIds,
    }),
  );
  for (const tempErpQuoteItem of successTemporaryErpQuoteItems) {
    const message = `Imported ${
      tempErpQuoteItem.rootComponent.part.partNumber
    } ${
      tempErpQuoteItem.rootComponent.part.revision
        ? `rev ${tempErpQuoteItem.rootComponent.part.revision}`
        : ''
    } from your ERP`;
    yield call(notifySuccess, message);
  }
  for (const tempErpQuoteItem of failureTemporaryErpQuoteItems) {
    const message = `Couldn't import ${
      tempErpQuoteItem.rootComponent.part.partNumber
    } ${
      tempErpQuoteItem.rootComponent.part.revision
        ? `rev ${tempErpQuoteItem.rootComponent.part.revision}`
        : ''
    } from your ERP`;
    yield call(notifyError, message);
  }
}

function* watchReplaceTempConvertedFromQuoteFileQuoteItems(action) {
  const { successes, failures } = action.payload;
  const successfulTaskIds = successes.map((result) => result.tempQuoteItemId);
  const failedTaskIds = failures.map((result) => result.tempQuoteItemId);
  const tasksToRemove = [...successfulTaskIds, ...failedTaskIds];
  const oldQuoteItems = yield select(getQuoteItems);
  const taskIds = [...successfulTaskIds, ...failedTaskIds];
  const tasks = yield select(getConvertQuoteFileToQuoteItemTasks);
  yield put(refreshQuote.request());
  yield put(
    removeConvertQuoteFileToQuoteItemTasks({
      tasks: tasksToRemove,
    }),
  );
  yield take([QUOTE_REFRESH_SUCCESS, QUOTE_REFRESH_ERROR]);

  // Remove temporary quote items (whose ids correspond to the task id)
  const tempQuoteItemsToRemove = oldQuoteItems.filter((qi) =>
    taskIds.includes(qi.id),
  );
  yield put(removeQuoteItems(tempQuoteItemsToRemove));

  const successfulFiles = [];
  for (const successfulTaskId of successfulTaskIds) {
    const { fileUuids } = tasks.find(
      ({ taskId }) => taskId === successfulTaskId,
    );
    successfulFiles.push(...fileUuids);
  }
  const failedFiles = [];
  for (const failedTaskId of failedTaskIds) {
    const { fileUuids } = tasks.find(({ taskId }) => taskId === failedTaskId);
    failedFiles.push(...fileUuids);
  }
  yield put(
    convertQuoteFilesToLineItems.finish({
      fileUuids: successfulFiles.concat(failedFiles),
    }),
  );

  if (failedTaskIds.length > 0) {
    if (successfulTaskIds.length > 0) {
      yield call(
        notifyWarning,
        "Couldn't convert some supporting files to line items",
      );
    } else {
      yield call(
        notifyError,
        "Couldn't convert supporting files to line items",
      );
    }
  }
}
